import { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/shared/api/firebase';
import type { AppUser } from '@/shared/types/domain';

// ── Types ─────────────────────────────────────────────────────
interface AuthContextValue {
  user: AppUser | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

// ── Context ───────────────────────────────────────────────────
const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setLoading(true);
      if (fbUser) {
        setFirebaseUser(fbUser);
        try {
          // ── 1. Get JWT token claims (force refresh for latest claims) ──
          const tokenResult = await fbUser.getIdTokenResult();
          const claims = tokenResult.claims as Record<string, unknown>;
          
          let systemRole = (claims['systemRole'] as string) ?? 'tenant_user';
          let tenantId   = claims['tenantId']   as string | undefined;
          let tenantRole = claims['tenantRole'] as string | undefined;
          let permissions= claims['permissions'] as Record<string,string> | undefined;

          // ── 2. Try to enrich with Firestore profile ────────────
          let displayName = fbUser.displayName ?? '';
          let photoURL: string | undefined;
          let assignedProcesses: string[] = [];
          let createdAt = new Date();
          let active = true;

          try {
            const userDoc = await getDoc(doc(db, 'users', fbUser.uid));
            if (userDoc.exists()) {
              const data = userDoc.data();
              
              if (data['active'] === false) {
                await firebaseSignOut(auth);
                setUser(null);
                setFirebaseUser(null);
                setLoading(false);
                return;
              }
              
              displayName        = data['displayName']       ?? displayName;
              photoURL           = data['photoURL']          ?? undefined;
              assignedProcesses  = data['assignedProcesses'] ?? [];
              createdAt          = data['createdAt']?.toDate() ?? new Date();
              active             = data['active'] ?? true;
              
              // Fallback to Firestore profile if claims are not populated yet:
              if (!tenantId) tenantId = data['tenantId'];
              if (!tenantRole) tenantRole = data['tenantRole'];
              if (!permissions) permissions = data['permissions'];
              if (systemRole === 'tenant_user' && data['systemRole']) {
                systemRole = data['systemRole'];
              }
            } else {
              // User document was deleted from Firestore (User Deleted!)
              await firebaseSignOut(auth);
              setUser(null);
              setFirebaseUser(null);
              setLoading(false);
              return;
            }
          } catch {
            // Firestore read failed — still OK, we have claims
            console.warn('Could not load Firestore profile, using token claims only');
          }

          setUser({
            uid:              fbUser.uid,
            email:            fbUser.email ?? '',
            displayName,
            photoURL,
            systemRole:       systemRole as AppUser['systemRole'],
            tenantId,
            tenantRole:       tenantRole  as AppUser['tenantRole'],
            permissions:      permissions as AppUser['permissions'],
            assignedProcesses,
            active,
            createdAt,
          });


        } catch (error) {
          console.error('Auth error:', error);
          setUser(null);
        }
      } else {
        setFirebaseUser(null);
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);



  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
    setUser(null);
    setFirebaseUser(null);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  return (
    <AuthContext.Provider value={{ user, firebaseUser, loading, signIn, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
